import React from "react";
import { motion } from "framer-motion";
import { BottomSheet } from "@/components/ui/bottom-sheet";
import { Check } from "lucide-react";

interface TeluaseghStorySheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const milestones = [
  {
    year: "2020",
    title: "A Magical Beginning",
    description: "TEL U ASEGH was born in Yerevan, during a serendipitous moment that would change everything."
  },
  {
    year: "2020-2023",
    title: "Building the Foundation",
    description: "A journey between Armenia and the UAE, meticulously crafting the brand's identity and vision."
  },
  {
    year: "2023",
    title: "Digital Innovation",
    description: "Ibrahim Gamal joins as Head of IT, building the technological foundation for an innovative luxury fashion experience."
  },
  {
    year: "Present",
    title: "Growing Vision",
    description: "Expanding beyond children's wear into men's, women's, and accessories collections."
  }
];

const MilestoneSection = ({ milestone, index }: { milestone: typeof milestones[0], index: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    viewport={{ once: true }}
    className="relative"
  >
    <div className="flex items-center gap-3 mb-2">
      <Check className="w-5 h-5 text-primary" />
      <div className="flex items-baseline gap-2">
        <span className="text-sm font-medium text-primary">{milestone.year}</span>
        <h3 className="text-lg font-cinzel font-bold text-primary">{milestone.title}</h3>
      </div>
    </div>
    <p className="text-sm text-muted-foreground pl-8">{milestone.description}</p>
  </motion.div>
);

export function TeluaseghStorySheet({ isOpen, onClose }: TeluaseghStorySheetProps) {
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <div className="px-4">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-3 tracking-wide font-cinzel">
            Story of TEL U ASEGH
          </h2>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            Where Love Meets Craftsmanship
          </p>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8 text-center"
        >
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            In 2020, during a magical moment in Yerevan, TEL U ASEGH was born—not just as a fashion brand, but as a vision deeply rooted in love, passion, and craftsmanship.
          </p>
        </motion.div>

        {/* Milestones */}
        <div className="space-y-8 mb-8">
          {milestones.map((milestone, index) => (
            <MilestoneSection
              key={index}
              milestone={milestone}
              index={index}
            />
          ))}
        </div>

        {/* Vision Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="bg-primary/5 backdrop-blur-sm rounded-2xl p-6 text-center"
        >
          <p className="text-sm text-muted-foreground mb-3">
            Our journey continues with a commitment to craftsmanship, innovation, and the celebration of cultural heritage.
          </p>
          <p className="text-sm font-medium text-primary">
            Join us in writing the next chapter of the TEL U ASEGH story.
          </p>
        </motion.div>
      </div>
    </BottomSheet>
  );
} 